import { getUserExternalDirectoryId } from 'accelerator-aad/lib/getUserExternalDirectoryId';
import { getItem, setItem, removeItem, type LocalStorageKeys } from 'owa-local-storage';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste
 *	> 'createAccessors' import from 'owa-viewstate-store' is restricted. createAccessors can cause performance problems. Create mutators by hand */
import { createAccessors } from 'owa-viewstate-store';
import { createStore } from 'owa-satcheljs';
import { mutatorAction } from 'satcheljs';

import type { Place } from 'owa-graph-schema';

/**
 * This function returns the key for the tenantHasBuildings property in local storage
 */
export function getTenantHasBuildingsKey(): LocalStorageKeys {
    const ownerId = getUserExternalDirectoryId();
    return `${ownerId}-TENANT_HAS_BUILDINGS`;
}

const getStore = createStore('places-places-store', {
    places: new Map<string, Place>(), // Map of all the buildings as the Place object, key is building id
    tenantHasBuildings: undefined as boolean | undefined,
    placesServiceCallsCompleted: new Map<string, boolean>(), // Key can either be buildingId or "all" for all places in the tenant
});

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function getStore called at module scope is not allowed. This may cause unwanted side effects. */
const store = getStore();

const dest = createAccessors('places-places-store', getStore);
const getPlaces: () => Map<string, Place> = dest.getPlaces;
const getTenantHasBuildings: () => boolean | undefined = dest.getTenantHasBuildings;
const setTenantHasBuildings: (newValue: boolean | undefined) => void = dest.setTenantHasBuildings;
const getPlacesServiceCallsCompleted: () => Map<string, boolean> =
    dest.getPlacesServiceCallsCompleted;

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setPlace: (buildingId: string, place: Place) => void = mutatorAction(
    'setPlace',
    (buildingId: string, place: Place): void => {
        store.places.set(buildingId, place);
    }
);

export function setTenantHasBuildingsCacheValue(): void {
    const tenantHasBuildingsCacheValue = getItem(window, getTenantHasBuildingsKey());
    if (tenantHasBuildingsCacheValue) {
        try {
            const tenantHasBuildings = JSON.parse(tenantHasBuildingsCacheValue);
            if (typeof tenantHasBuildings === 'boolean') {
                setTenantHasBuildings(tenantHasBuildings);
            } else {
                throw new Error('Invalid value');
            }
        } catch {
            removeItem(window, getTenantHasBuildingsKey());
        }
    }
}

export function setTenantHasBuildingsStoreAndCache(value: boolean): void {
    setTenantHasBuildings(value);
    setItem(window, getTenantHasBuildingsKey(), JSON.stringify(value));
}

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const addServiceCallsCompleted: (placeServiceCallType: string, isSuccess: boolean) => void =
    mutatorAction(
        'addServiceCallsCompleted',
        (placeServiceCallType: string, isSuccess: boolean): void => {
            const serviceCalls = getPlacesServiceCallsCompleted();
            serviceCalls.set(placeServiceCallType, isSuccess);
            getStore().placesServiceCallsCompleted = serviceCalls;
        }
    );

export { getPlaces, getTenantHasBuildings, getPlacesServiceCallsCompleted };
